<script>
import { computed } from 'vue';
import debounce from 'lodash/debounce';

import PError from './partials/PError';
import PLabel from './partials/PLabel';

export default {
  name: 'p-number',
  components: {
    PLabel,
    PError
  },
  props: {
    disabled: { type: Boolean, default: false },
    error: { type: String, default: '' },
    extra: { type: Number, default: 0 },
    focus: { type: Boolean, default: false },
    label: { type: String, default: '' },
    max: { type: Number, default: Number.POSITIVE_INFINITY },
    min: { type: Number, default: 0 },
    placeholder: { type: String, default: '' },
    readonly: { type: Boolean, default: false },
    size: { type: String, default: 'normal', validator: (value) => ['small', 'normal'].indexOf(value) !== -1 },
    step: { type: Number, default: 1 },
    value: { type: Number, default: 0 },
    wait: { type: Number, default: 0 },
    warn: { type: Number, default: 0 },
    warnBelow: { type: Number, default: 0 },
    warnMessage: { type: String, default: '' },
  },
  emits: ['input', 'wrongStepValue'],
  setup(props) {
    const tabIndex = computed(() => props.disabled || props.readonly ? '-1' : '')
    const increasable = computed(() => {
      const num = props.value;
      return Number.isNaN(num) || num + props.step <= props.max;
    })
    const decreasable = computed(() => {
      const num = props.value;
      return Number.isNaN(num) || num - props.step >= props.min;
    })
    const warnActive = computed(() => {
      return (
        props.value > 0 &&
        ((props.warn > 0 && props.value >= props.warn) ||
          (props.warnBelow > 0 && props.value <= props.warnBelow))
      );
    })
    const inputClasses = computed(() => {
      return {
        'bg-gray-50 border-gray-300 text-gray-400 cursor-not-allowed':
          props.disabled,
        'border-gray-400 focus:border-white focus:shadow-outline':
          !props.error && !props.disabled && !props.readonly,
        'border-red-500 focus:border-red-600 placeholder-red-300 text-red-600':
          props.error,
        'p-2 h-10':
          props.size === 'normal',
        'p-1 h-8 text-xs':
          props.size === 'small',
        'text-right text-gray-500':
          props.readonly,
        'text-right pr-8':
          !props.readonly
      };
    })

    return {
      tabIndex, increasable, decreasable, warnActive, inputClasses
    }
  },
  computed: {
    onInput() {
      return debounce(this.inputCaptured, this.wait);
    },
  },
  methods: {
    onKeyDown(event) {
      const code = event.keyCode;
      const isValid =
        (code >= 48 && code <= 57) || // 0-9
        (code >= 96 && code <= 105) || // 0-9 numpad
        (code >= 37 && code <= 40) || // arrows
        code === 9 || // tab
        code === 8 || // backspace
        code === 46 || // delete
        (code === 109 && this.min < 0) || // minus (numpad)
        (code === 189 && this.min < 0); // minus
      if (!isValid) event.preventDefault();
    },
    increase() {
      if (!this.increasable) return;
      if (Number.isNaN(this.value)) {
        this.$emit('input', 0);
      }
      this.$emit('input', Number.parseInt(this.value, 10) + this.step);
    },
    decrease() {
      if (!this.decreasable) return;
      if (this.value > this.min) {
        this.$emit('input', Number.parseInt(this.value, 10) - this.step);
      }
    },
    inputCaptured() {
      const refValue = Number.parseInt(this.$refs.input.value);
      let num = Number.isNaN(refValue) ? 0 : refValue;
      if (refValue === this.value) return

      if (this.max !== Number.POSITIVE_INFINITY) {
        const max = Number.parseInt(this.max, 10) - this.extra;
        if (num > max) num = max;
      }
      // !! Must be calculated before adjusting to valid step value
      const isCorrectStepValue = (num - this.extra) % this.step === 0

      // console.log('Antall TPUer', Number.parseInt((num - this.extra) / this.step, 10))
      if (num !== 0) {
        num = Number.parseInt((num - this.extra) / this.step, 10) * this.step + this.extra;
      }

      this.$refs.input.value = num;
      this.$emit('input', num);

      // !! Must be after emit('input')
      if (!isCorrectStepValue) this.$emit('wrongStepValue')
    }
  },
  mounted() {
    if (this.focus) {
      this.$refs.input.focus();
    }
  }
};
</script>

<template>
  <div>
    <PLabel
      :label="label"
      :error="error"
      @click="$refs.input.focus()"
    />
    <div class="relative group">
      <FontAwesomeIcon
        v-if="warnActive"
        :icon="['far', 'exclamation-triangle']"
        class="absolute ml-2 text-sm text-yellow-500 top-1/2"
        v-tooltip="{ content: warnMessage, delay: { show: 100, hide: 100 } }"
      />
      <input
        ref="input"
        :class="inputClasses"
        :data-extra="extra"
        :data-max="max"
        :data-min="min"
        :data-step="step"
        :disabled="disabled"
        :placeholder="placeholder"
        :readonly="readonly"
        :tabindex="tabIndex"
        :value="value"
        class="w-full leading-none border rounded shadow-inner focus:outline-none"
        @blur="inputCaptured"
        @focus="(event) => event.target.select()"
        @input="onInput"
        @keydown.down="decrease"
        @keydown.up="increase"
        @keydown="onKeyDown"
      />
      <button
        v-if="!readonly"
        :class="{
          'cursor-not-allowed text-gray-400 border-gray-300': disabled || max === 0 || value === max,
          'text-gray-500 border-gray-400 over:text-gray-900 group-focus:border-gray-500': !disabled && max !== 0 && value < max
        }"
        :disabled="disabled || value >= max"
        class="absolute top-0 right-0 flex items-center justify-center w-6 border-b border-l h-1/2 focus:outline-none"
        tabindex="-1"
        @click.prevent="increase"
      >
        <FontAwesomeIcon
          class="text-xs"
          :icon="['far', 'chevron-up']"
        />
      </button>
      <button
        v-if="!readonly"
        :class="{
          'cursor-not-allowed text-gray-400 border-gray-300':
            disabled || max === 0 || value === min,
          'text-gray-500 border-gray-400 hover:text-gray-900 group-focus:border-gray-500':
            !disabled && max !== 0 && value > min
        }"
        :disabled="disabled || value === min"
        class="absolute bottom-0 right-0 flex items-center justify-center w-6 border-l h-1/2 focus:outline-none"
        tabindex="-1"
        @click.prevent="decrease"
      >
        <FontAwesomeIcon
          class="text-xs"
          :icon="['far', 'chevron-down']"
        />
      </button>
    </div>
    <PError
      v-if="error"
      :error="error"
    />
  </div>
</template>
